import {useMutation} from '@apollo/react-hooks';

import type {
    ContactUpdateInputFieldInput as ContactUpdateInput,
    ContactInfoFragment as ContactInfo,
    UpdateContactInfo as UpdateContactInfoMutation,
    UpdateContactInfoVariables as UpdateContactInfoMutationVariables,
} from 'nutshell-graphql-types';

import {nutApolloClient} from '../../../../apollo-client';

import CONTACT_INFO_FRAGMENT from './graphql/fragments/contact-info-fragment.graphql';
import UPDATE_CONTACT_INFO from './graphql/mutations/update-contact-info.graphql';

export function useUpdateContactInfo(): (arg1: ContactUpdateInput) => Promise<any> {
    const [updateContactInfo] = useMutation<
        UpdateContactInfoMutation,
        UpdateContactInfoMutationVariables
    >(UPDATE_CONTACT_INFO);

    return (input: ContactUpdateInput) => {
        // Read current contact info to not blow away existing values that are not being updated.
        const contactInfo = nutApolloClient.readFragment({
            id: input.id,
            fragment: CONTACT_INFO_FRAGMENT,
            fragmentName: 'ContactInfoFragment',
        });

        return updateContactInfo({
            variables: {input: {contact: input}},
            optimisticResponse: contactInfo
                ? {
                      // @ts-expect-error
                      __typename: 'Mutation',
                      contactUpdate: {
                          __typename: 'ContactUpdatePayload',
                          contact: {
                              ...getUpdatedContactInfoFromInput(input, contactInfo),
                          },
                      },
                  }
                : undefined,
            update: (proxy, mutationResults) => {
                const updatedContactInfo =
                    mutationResults &&
                    mutationResults.data &&
                    mutationResults.data.contactUpdate &&
                    mutationResults.data.contactUpdate.contact;

                if (!updatedContactInfo) return;

                proxy.writeFragment({
                    id: input.id,
                    fragment: CONTACT_INFO_FRAGMENT,
                    fragmentName: 'ContactInfoFragment',
                    data: {
                        ...updatedContactInfo,
                    },
                });
            },
        });
    };
}

const getUpdatedContactInfoFromInput = (
    input: ContactUpdateInput,
    contactInfo: ContactInfo
): ContactInfo => {
    const updatedContactInfo: ContactInfo = {...contactInfo};

    Object.keys(input).forEach((fieldKey) => {
        switch (fieldKey) {
            case 'ownerId':
                updatedContactInfo.owner = input.ownerId
                    ? {
                          id: input.ownerId,
                          __typename: input.ownerId.includes('users') ? 'User' : 'Team',
                      }
                    : null;
                break;
            case 'territoryId':
                updatedContactInfo.territory = input.territoryId
                    ? {
                          __typename: 'Territory',
                          id: input.territoryId,
                      }
                    : null;
                break;
        }
    });

    return updatedContactInfo;
};
